/**
 * Gapless playback of streamed PCM (s16le mono) for listeners.
 *
 * Chunks are scheduled back to back on the Web Audio clock. If the voice
 * falls behind the speaker (translations are often longer than the original),
 * playback speeds up a little; if it falls far behind, new audio is skipped
 * until it is close to live again. Attendees hear the sermon, not a delayed
 * recording of it.
 */

export type ClockOptions = {
  leadS?: number;
  /** Buffer held before speech starts (after silence), so chunks play without gaps. */
  delayS?: number;
  speedUpAfterS?: number;
  skipAfterS?: number;
  fastRate?: number;
};

export class PlaybackClock {
  private next = 0;
  /** Seconds from "spoken" to "played", once the speaker's timeline is known. */
  private offset: number | null = null;
  /** Slot for the first chunk of the sentence that just started. */
  private target: number | null = null;
  private readonly lead: number;
  private delay: number;
  private readonly speedUpMargin: number;
  private readonly skipMargin: number;
  private readonly fastRate: number;
  dropped = 0;

  constructor(options: ClockOptions = {}) {
    this.lead = options.leadS ?? 0.08;
    this.delay = Math.max(0, options.delayS ?? 0);
    // Catch-up thresholds sit beyond the chosen buffer, not inside it.
    this.speedUpMargin = options.speedUpAfterS ?? 6;
    this.skipMargin = options.skipAfterS ?? 15;
    this.fastRate = options.fastRate ?? 1.12;
  }

  setDelay(seconds: number): void {
    const next = Math.max(0, seconds);
    if (this.offset !== null) this.offset = Math.max(this.lead, this.offset + next - this.delay);
    this.delay = next;
  }

  /**
   * A sentence starts; `ageS` = how long ago (speaker time) it began being spoken.
   * It is played at "spoken at + offset", so the silences in the translation
   * follow the speaker's pauses instead of processing hiccups. The offset starts
   * at the first sentence's processing time + the delay, grows when a sentence
   * arrives late (later ones keep pace without a gap), and eases back when
   * processing gets faster again, so a long sermon does not drift ever later.
   */
  segment(now: number, ageS: number): void {
    const needed = ageS + this.lead;
    if (this.offset === null) this.offset = ageS + this.delay;
    else if (needed > this.offset) this.offset = needed;
    else if (this.offset > ageS + this.delay) this.offset -= 0.1 * (this.offset - (ageS + this.delay)); // ease back slowly
    this.target = now - ageS + this.offset;
  }

  get delayS(): number {
    return this.delay;
  }

  /** When to start a chunk of `durationS`, and at what rate; null means skip it. */
  schedule(now: number, durationS: number): { at: number; rate: number } | null {
    if (this.target !== null) {
      // First chunk of a sentence: its slot on the speaker's timeline.
      this.next = Math.max(this.next, this.target, now + this.lead);
      this.target = null;
    } else if (this.next <= now + this.lead) {
      // Ran dry. Paced by the speaker's timeline: just carry on. Without it
      // (older engine): refill the buffer before speaking again.
      this.next = now + (this.offset === null ? Math.max(this.lead, this.delay) : this.lead);
    }
    const lag = this.next - now;
    if (lag > this.delay + this.skipMargin) {
      this.dropped += 1;
      return null;
    }
    const rate = lag > this.delay + this.speedUpMargin ? this.fastRate : 1;
    const at = this.next;
    this.next += durationS / rate;
    return { at, rate };
  }

  backlog(now: number): number {
    return Math.max(0, this.next - now);
  }

  reset(): void {
    this.next = 0;
    this.offset = null;
    this.target = null;
  }
}

export function pcm16ToFloat(buffer: ArrayBuffer): Float32Array<ArrayBuffer> {
  const view = new DataView(buffer);
  const out = new Float32Array(Math.floor(buffer.byteLength / 2));
  for (let i = 0; i < out.length; i += 1) out[i] = view.getInt16(i * 2, true) / 32768;
  return out;
}

export class PcmPlayer {
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private clock = new PlaybackClock({ delayS: 3 });
  private sources = new Set<AudioBufferSourceNode>();
  sampleRate = 22050;

  get active(): boolean {
    return this.ctx !== null && this.ctx.state !== "closed";
  }

  /** Must run inside a user gesture (tap/click), or browsers keep audio muted. */
  async unlock(): Promise<void> {
    // iOS: play even with the ring/silent switch on (Safari 16.4+).
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session) session.type = "playback";
    if (!this.ctx || this.ctx.state === "closed") {
      this.ctx = new AudioContext({ latencyHint: "playback" });
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
      this.clock.reset();
    }
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  push(chunk: ArrayBuffer): void {
    const ctx = this.ctx;
    if (!ctx || !this.gain || chunk.byteLength < 2) return;
    if (ctx.state === "suspended") void ctx.resume().catch(() => undefined);
    const samples = pcm16ToFloat(chunk);
    const slot = this.clock.schedule(ctx.currentTime, samples.length / this.sampleRate);
    if (!slot) return;
    const buffer = ctx.createBuffer(1, samples.length, this.sampleRate);
    buffer.copyToChannel(samples, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = slot.rate;
    source.connect(this.gain);
    source.onended = () => this.sources.delete(source);
    this.sources.add(source);
    source.start(slot.at);
  }

  backlog(): number {
    return this.ctx ? this.clock.backlog(this.ctx.currentTime) : 0;
  }

  /** Seconds of speech held back before playing (set by the speaker for the session). */
  setDelay(seconds: number): void {
    this.clock.setDelay(seconds);
  }

  /** A sentence begins that started being spoken `ageS` seconds ago. */
  segmentStart(ageS: number): void {
    if (this.ctx) this.clock.segment(this.ctx.currentTime, Math.max(0, ageS));
  }

  async stop(): Promise<void> {
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        /* already ended */
      }
    }
    this.sources.clear();
    const ctx = this.ctx;
    this.ctx = null;
    this.gain = null;
    this.clock.reset();
    await ctx?.close().catch(() => undefined);
  }
}
